import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import {
  FaBox,
  FaDesktop,
  FaChartLine,
  FaBullhorn,
  FaBuilding,
  FaFileAlt,
  FaFileInvoice,
  FaEllipsisH,
  FaTimes,
  FaUpload,
  FaCheck,
  FaInfoCircle,
  FaPlus,
  FaTrash,
  FaEdit,
  FaPaperclip,
} from "react-icons/fa";
import { Theme } from "../../lib/theme";
import { useNavigate } from "react-router-dom";
import { IpInfoResponse } from "../../types/authTypes";
import { Plan } from "../../types/pricingTypes";
// PricingModal removed — use external pricing page
import { openPricing } from '../../utils/openPricing'
import { useCredits } from '../../hooks/useCredits'
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setUserPlan } from "../../redux/slices/userSlice";
import uploadFileToS3 from "../../utils/uploadFileToS3";
import GuidedTour from "../onboarding/shared/GuidedTour";
import { setIsOutlineGenerated } from "../../redux/slices/outlineSlice";
import GuidedTourMobile from "../onboarding/shared/GuidedTourMobile";
import { Dialog } from "@headlessui/react";
import WebsiteLinkForm from "../onboarding/onboarding-sections/WebsiteLinkForm";

// Import environment variables
const API_URL =
  process.env.REACT_APP_BACKEND_URL || "https://d2bwumaosaqsqc.cloudfront.net";

// Define an interface for colors
interface Colors {
  P100: string;
  P75_S25: string;
  P50_S50: string;
  P25_S75: string;
  S100: string;
  F_P100: string;
  F_P75_S25: string;
  F_P50_S50: string;
  F_P25_S75: string;
  F_S100: string;
  SCL: string;
  SCD: string;
}
const supportedLanguages = [
  "English",
  "Chinese (Simplified)",
  "Chinese (Traditional)",
  "Spanish",
  "French",
  "German",
  "Portuguese (Brazilian)",
  "Portuguese (European)",
  "Italian",
  "Russian",
  "Japanese",
  "Korean",
  "Dutch",
  "Arabic (Modern Standard)",
  "Polish",
  "Turkish",
  "Vietnamese",
];
interface SlideOutline {
  slideName?: any;
  title: string;
  type: string;
  keywords?: string;
  method?: string;
  outlineID: string;
}
const outlineTest: SlideOutline[] = [
  {
    title: "Introduction to Labor Movements",
    type: "Points",
    keywords: "Industrial revolution, Workers’ unions",
    outlineID: "outlineID-1a2b3c4d-1111-aaaa-2222-333344445555",
  },
  {
    title: "Early Struggles and Resistance",
    type: "Points",
    keywords: "Protests, Wage disputes, Exploitation",
    outlineID: "outlineID-2b3c4d5e-2222-bbbb-3333-444455556666",
  },
  {
    title: "Rise of Trade Unions",
    type: "Points",
    keywords: "Collective bargaining, Unionization",
    outlineID: "outlineID-3c4d5e6f-3333-cccc-4444-555566667777",
  },
  {
    title: "Legal Milestones in Labor Rights",
    type: "Points",
    keywords: "Labor laws, Minimum wage, Child labor bans",
    outlineID: "outlineID-4d5e6f70-4444-dddd-5555-666677778888",
  },
  {
    title: "Modern Challenges in the Workforce",
    type: "Points",
    keywords: "Gig economy, Job insecurity, Automation",
    outlineID: "outlineID-5e6f7081-5555-eeee-6666-777788889999",
  },
  {
    title: "Global Perspectives on Labor Reforms",
    type: "Points",
    keywords: "ILO, International standards, Developing nations",
    outlineID: "outlineID-6f708192-6666-ffff-7777-888899990000",
  },
  {
    title: "Conclusion and Future Outlook",
    type: "Points",
    keywords: "Sustainable labor, Equity, Digital rights",
    outlineID: "outlineID-7f8192a3-7777-aaaa-8888-999900001111",
  },
];

const DocumentInput: React.FC = () => {
  const presentationTypes = [
    { id: 1, label: "Product", icon: <FaBox className="text-[#3667B2]" /> },
    {
      id: 2,
      label: "Pitch Deck",
      icon: <FaDesktop className="text-[#3667B2]" />,
    },
    {
      id: 3,
      label: "Sales Deck",
      icon: <FaChartLine className="text-[#3667B2]" />,
    },
    {
      id: 4,
      label: "Marketing",
      icon: <FaBullhorn className="text-[#3667B2]" />,
    },
    {
      id: 5,
      label: "Company Overview",
      icon: <FaBuilding className="text-[#3667B2]" />,
    },
    {
      id: 6,
      label: "Project Proposal",
      icon: <FaFileInvoice className="text-[#3667B2]" />,
    },
    {
      id: 7,
      label: "Project Summary",
      icon: <FaFileAlt className="text-[#3667B2]" />,
    },
    {
      id: 8,
      label: "Others",
      icon: <FaEllipsisH className="text-[#3667B2]" />,
    },
  ];
  const [isRefineModalOpen, setIsRefineModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleColorChange = (
    key: keyof typeof brandingColors,
    color: string
  ): void => {
    setBrandingColors((prev) => ({ ...prev, [key]: color }));
  };
  const [file, setFile] = useState<File | null>(null);
  const userAgent = navigator.userAgent.toLowerCase();
  const dispatch = useDispatch();
  const [theme, setTheme] = useState("Light");
  const [creativity, setCreativity] = useState(5);
  const [pdfLink, setPdfLink] = useState("");
  const [pdfUploading, setPDFUploading] = useState(false);
  const [selectedType, setSelectedType] = useState<number | null>(null);
  const [customTypeInput, setCustomTypeInput] = useState<string>("");
  const navigate = useNavigate();
  const authToken = sessionStorage.getItem("authToken");
  const orgId = sessionStorage.getItem("orgId");
  const { credits, loading: creditsLoading, ensureCredits, deductOptimistic, setCredits, refresh } = useCredits();
  const MIN_DOCUMENT = 10;
  // pricing modal removed — redirects to external pricing page when needed
  const pricingModalHeading = "Refine PPT";
  const userPlan = useSelector((state: any) => state.user.userPlan);
  const [monthlyPlan, setMonthlyPlan] = useState<Plan>();
  const [yearlyPlan, setYearlyPlan] = useState<Plan>();
  const [currency, setCurrency] = useState("");
  const [subId, setSubId] = useState("");
  const [eligibleForGeneration, setEligibleForGeneration] = useState(false);
  const [showThemeTooltip, setShowThemeTooltip] = useState(false);
  const [hoveredTheme, setHoveredTheme] = useState<string | null>(null); // Track which theme is hovered
  const [pptCount, setPptCount] = useState(0);
  const [pptCountMonthly, setPptCountMonthly] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showTooltip1, setShowTooltip1] = useState(false);
  const [showTooltip2, setShowTooltip2] = useState(false);
  const [showTooltip3, setShowTooltip3] = useState(false);
  const [showTooltip4, setShowTooltip4] = useState(false);
  const [showTooltip5, setShowTooltip5] = useState(false);
  const [showReferenceUrlTooltip, setShowReferenceUrlTooltip] = useState(false);
  const [showWebSearchTooltip, setShowWebSearchTooltip] = useState(false);
  const [showTechnicalDepthTooltip, setShowTechnicalDepthTooltip] =
    useState(false);
  const [showCitationsTooltip, setShowCitationsTooltip] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState<string>("");
  const [isHovered, setIsHovered] = useState(false);
  const [primaryColor, setPrimaryColor] = useState("#000000"); // Define the primary color
  const [textColor, setTextColor] = useState("#000000"); // Define the primary color
  const [secondaryColor, setSecondaryColor] = useState("#FFFFFF"); // Define the secondary color
  const [generateInput, setGenerateInput] = useState("");
  // New presentation options state
  const [wordLength, setWordLength] = useState<string>(
    "Brief Note(300-500 words)"
  ); // Default word length
  const [includeImages, setIncludeImages] = useState<boolean>(false); // Default to include images
  const [targetLanguage, setTargetLanguage] = useState<string>("Auto"); // Default language
  const [presentationTone, setPresentationTone] = useState<string>("Auto"); // Default tone
  const [targetAudience, setTargetAudience] = useState<string>("Auto"); // Default audience

  // New inner modal presentation options state
  const [referenceWebUrl, setReferenceWebUrl] = useState<string>(""); // Default reference web URL
  const [includeWebSearch, setIncludeWebSearch] = useState<boolean>(false); // Default web search toggle
  const [technicalDepth, setTechnicalDepth] = useState<string>("Auto"); // Default technical depth
  const [includeCitations, setIncludeCitations] = useState<boolean>(false); // Default citations toggle

  const [loading, setLoading] = useState(false);
  const [outline, setOutline] = useState<SlideOutline[]>([]);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [generatedDocumentIDoutline, setGeneratedDocumentIDoutline] = useState<
    string | null
  >(null);
  const [brandingColors, setBrandingColors] = useState<string[]>([]); // Initialize branding colors
  const [brandingColorsSave, setBrandingColorsSave] = useState<string[]>([]); // Initialize branding colors
  const [isLoading, setIsLoading] = useState<boolean>(false); // Add a loading state
  const [isValidLink, setIsValidLink] = useState(false);
  const [organizationColors, setOrganizationColors] = useState<Colors | null>(
    null
  );
  const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
  const [language, setLanguage] = useState<string>("English");
  const [inputIndex, setInputIndex] = useState<number | null>(null);
  const [newOutline, setNewOutline] = useState<string>("");
  const [newOutlineLoading, setNewOutlineLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"outline" | "options">("options");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedOutline, setEditedOutline] = useState<{
    title: string;
    keywords: string;
  }>({ title: "", keywords: "" });
  const [newOutlineKeywords, setNewOutlineKeywords] = useState<string>("");

  const fetchOutlines = useCallback(async () => {
    if (!generatedDocumentIDoutline) return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/outline/${generatedDocumentIDoutline}/outline`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const { outline: fetchedOutlines } = response.data;

      setOutline(fetchedOutlines);
    } catch (error) {
      console.error("Error fetching outlines:", error);
    }
  }, [generatedDocumentIDoutline, authToken]);

  const handleAddOutline = async (index: number) => {
    const newOutlineID = generateOutlineID();

    setNewOutlineLoading(true);
    if (!newOutline.trim()) {
      console.log("New outline is empty");
      setNewOutlineLoading(false);
      return;
    }

    // Create the new outline object
    const newOutlineItem = {
      title: newOutline,
      type: "Points", // Default type
      keywords: newOutlineKeywords,
      outlineID: newOutlineID,
    };

    // Optimistically update the local state
    const updatedOutlines = [...outline];
    updatedOutlines.splice(index + 1, 0, newOutlineItem);
    setOutline(updatedOutlines);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/outline/document-generate/blocklist/insert`,
        {
          documentId: generatedDocumentIDoutline,
          title: newOutline,
          keywords: newOutlineKeywords,
          position: index + 1,
          outlineID: newOutlineID,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const result = response.data;
      if (result.title && result.type) {
        // Retrieve the existing outlineIDs array from sessionStorage
        const storedOutlineIDs = sessionStorage.getItem("outlineIDs");
        let outlineIDs = storedOutlineIDs ? JSON.parse(storedOutlineIDs) : [];

        // Add the new outlineID to the array
        outlineIDs.push(newOutlineID);

        // Save the updated array back to sessionStorage
        sessionStorage.setItem("outlineIDs", JSON.stringify(outlineIDs));
        toast.success("Outline Successfully Added", {
          position: "top-right",
          autoClose: 3000,
        });

        // Fetch fresh data from server to ensure consistency
        await fetchOutlines();

        // Notify document view component to refresh outlines
        dispatch(setIsOutlineGenerated(true));

        setNewOutlineLoading(false);
      }
      setInputIndex(null);
      setNewOutline("");
      setNewOutlineKeywords("");
    } catch (error: any) {
      // Rollback the optimistic update on error
      setOutline(outline);

      if (error.response && error.response.status === 404) {
        toast.error("Upgrade to add more slides", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        toast.error("Error adding outline", {
          position: "top-right",
          autoClose: 3000,
        });
      }
      console.error("Error adding outline:", error);
      setNewOutlineLoading(false);
    }
  };

  const handleDeleteOutline = async (index: number) => {
    if (!generatedDocumentIDoutline) return;

    setOutline((prevOutlines) => {
      const updatedOutlines = [...prevOutlines];
      const deletedItem = updatedOutlines.splice(index, 1);

      (async () => {
        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/v1/outline/blocklist/delete`,
            {
              documentId: generatedDocumentIDoutline,
              index,
            },
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          );
          toast.success("Outline deleted successfully", {
            position: "top-right",
            autoClose: 3000,
          });

          // Notify document view component to refresh outlines
          dispatch(setIsOutlineGenerated(true));
        } catch (error) {
          // Rollback on failure
          setOutline((current) => {
            const rollbackOutlines = [...current];
            rollbackOutlines.splice(index, 0, deletedItem[0]);
            return rollbackOutlines;
          });

          toast.error("Failed to delete outline. Please try again.", {
            position: "top-right",
            autoClose: 3000,
          });

          console.error("Error deleting outline:", error);
        }
      })();

      return updatedOutlines;
    });
  };

  const handleCloseModal = () => {
    // Don't reset the document ID to preserve the outline state
    // setGeneratedDocumentIDoutline(null); // Reset ID when closing
    setIsRefineModalOpen(false);
  };
  const generateOutlineID = () => {
    return `outlineID-${window.crypto.randomUUID()}`;
  };
  useEffect(() => {
    if (websiteUrl) {
      setIsValidLink(urlRegex.test(websiteUrl));
    }
  }, [websiteUrl]); // Re-run validation when `websiteUrl` changes

  const generateDocumentID = () => {
    return "Document-" + Date.now();
  };
  const generatedDocumentID = generateDocumentID();

  const MAX_FILE_SIZE_MB = 20; // Limit file size to 20MB

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPDFUploading(true);

    if (event.target.files && event.target.files[0]) {
      const uploadedFile = event.target.files[0];

      // Check file size
      const fileSizeMB = uploadedFile.size / (1024 * 1024);
      if (fileSizeMB > MAX_FILE_SIZE_MB) {
        toast.error(
          `File size exceeds ${MAX_FILE_SIZE_MB}MB. Please upload a smaller file.`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
        setPDFUploading(false);
        return;
      }

      setFile(uploadedFile);

      try {
        const pdfUploaded = {
          name: uploadedFile.name,
          type: uploadedFile.type,
          body: uploadedFile, // Pass the File object directly
        };
        // Handle upload
        const pdfLink = await uploadFileToS3(pdfUploaded);
        setPdfLink(pdfLink);
      } catch (error) {
        console.error("Error uploading file:", error);
        toast.error("Failed to upload file. Please try again.", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setPDFUploading(false);
      }
    } else {
      setPDFUploading(false);
    }
  };

  const handleGenerate = async () => {
    // Pre-flight credit check
    if (creditsLoading) {
      toast.info("Checking credits, please wait...", {
        position: "top-right",
        autoClose: 2000,
      });
      return;
    }

    if (!ensureCredits(MIN_DOCUMENT)) {
      openPricing('documentinput')
      return;
    }

    // Optimistically deduct credits for immediate UI feedback
    const prevCredits = credits ?? 0;
    try {
      deductOptimistic(MIN_DOCUMENT);
      try { localStorage.setItem('zynth:credits_updated', String(Date.now())); } catch (e) {}
    } catch (e) {}

    navigate(`/new-document?documentID=${generatedDocumentIDoutline}`);

    const quickGenerate = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/documentgenerate/generate-document/${orgId}/${generatedDocumentIDoutline}`,
          {
            pptInput: generateInput,
          },
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        await response.data;
      } catch (error: any) {
        // rollback optimistic deduction
        try { setCredits(prevCredits); localStorage.setItem('zynth:credits_updated', String(Date.now())); } catch (e) {}
        if (error.response?.status === 404) {
          toast.error("Error generating document", {
            position: "top-right",
            autoClose: 3000,
          });
        }
        if (error.response?.status === 402) {
          openPricing('documentinput_credit')
        }
      }
    };

    const updatePptCount = async () => {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organizationedit/${orgId}`,
        {
          pptcount: pptCount + 1,
          pptcount_monthly: pptCountMonthly + 1,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
    };

    await quickGenerate();
    await updatePptCount();
    try { await refresh(); } catch (e) {}
  };
  const handleSaveColors = async () => {
    setIsLoading(true); // Show loading state

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile//organization/updateColors/${orgId}`,
        { primaryColor, secondaryColor },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (response.status === 200) {
        const colors = response.data.colors || organizationColors;

        // ✅ Build a colorMap with safe defaults
        const colorMap = {
          P100: colors.P100 || "#17191A",
          P75_S25: colors.P75_S25 || "#313B48",
          P50_S50: colors.P50_S50 || "#4A5C75",
          P25_S75: colors.P25_S75 || "#647EA3",
          S100: colors.S100 || "#7D9FD0",
          F_P100: colors.F_P100 || "#E8E8E8",
          F_P75_S25: colors.F_P75_S25 || "#E4E8EC",
          F_P50_S50: colors.F_P50_S50 || "#FFFFFF",
          F_P25_S75: colors.F_P25_S75 || "#FFFFFF",
          F_S100: colors.F_S100 || "#FFFFFF",
          SCL: colors.SCL || "#FFFFFF",
          SCD: colors.SCD || "#000000",
        };

        // Convert object values into arrays
        const colorArray: string[] = [
          colorMap.P100,
          colorMap.P75_S25,
          colorMap.P50_S50,
          colorMap.P25_S75,
          colorMap.S100,
        ];

        const colorArraySave: string[] = [
          colorMap.P100,
          colorMap.P75_S25,
          colorMap.P50_S50,
          colorMap.P25_S75,
          colorMap.S100,
          colorMap.F_P100,
          colorMap.F_P75_S25,
          colorMap.F_P50_S50,
          colorMap.F_P25_S75,
          colorMap.F_S100,
          colorMap.SCL,
          colorMap.SCD,
        ];

        await setBrandingColors([...colorArray]);
        await setBrandingColorsSave([...colorArraySave]);

        // ✅ Set individual color states
        setPrimaryColor(colorMap.P100);
        setTextColor(colorMap.F_P100);
        setSecondaryColor(colorMap.S100);
      }
    } catch (error) {
      console.error("Error updating branding colors:", error);
    } finally {
      setIsLoading(false); // Hide loading state
    }
  };

  // const handleRefinePPT = () => {
  //   const refinePPT = async () => {
  //     if (!file) {
  //       toast.info('Please upload a PDF to refine', {
  //         position: 'top-right',
  //         autoClose: 3000,
  //       })
  //       return
  //     }

  //     navigate(
  //       `/presentation-view?documentID=${generatedDocumentIDoutline}`
  //     )

  //     try {
  //       const response = await axios.post(
  //         `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/documentgenerate/generate-document/refineppt/${orgId}/${generatedDocumentIDoutline}`,
  //         {
  //           pdfLink: pdfLink,
  //         },
  //         {
  //           headers: {
  //             Authorization: `Bearer ${authToken}`,
  //           },
  //         }
  //       )

  //       await response.data
  //     } catch (error: any) {

  //     }
  //   }

  //   const updatePptCount = async () => {
  //     await axios.patch(
  //       `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organizationedit/${orgId}`,
  //       {
  //         pptcount: pptCount + 1,
  //         pptcount_monthly: pptCountMonthly + 1,
  //       },
  //       {
  //         headers: {
  //           Authorization: `Bearer ${authToken}`,
  //         },
  //       }
  //     )
  //   }
  //   refinePPT()
  //   updatePptCount()
  // }

  const updatePptCount = async () => {
    await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organizationedit/${orgId}`,
      {
        pptcount: pptCount + 1,
        pptcount_monthly: pptCountMonthly + 1,
      },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
  };
  const isButtonDisabled =
    selectedType === 8
      ? !customTypeInput.trim() ||
        (userPlan !== "pro" && !eligibleForGeneration)
      : !selectedType || (userPlan !== "pro" && !eligibleForGeneration);

  // API CALL TO GET PRICING DATA FOR MODAL AND USER PLAN
  useEffect(() => {
    const getPricingData = async () => {
      try {
        const ipInfoResponse = await fetch(
          "https://api.zynth.ai/api/v1/data/userprofile/ip-info"
        );
        const ipInfoData: IpInfoResponse = await ipInfoResponse.json();

        await axios
          .get(
            `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/appscripts/razorpay/plans`,
            {
              headers: {
                Authorization: `Bearer ${authToken}`,
              },
            }
          )
          .then((response) => {
            const country = ipInfoData!.country!;
            if (country !== "IN" && country !== "India" && country !== "In") {
              setMonthlyPlan(response.data.items[4]);
              setYearlyPlan(response.data.items[2]);
              setCurrency("USD");
            } else if (
              country === "IN" ||
              country === "India" ||
              country === "In"
            ) {
              setMonthlyPlan(response.data.items[5]);
              setYearlyPlan(response.data.items[3]);
              setCurrency("INR");
            }
          });
      } catch (error) {
        console.error("Error fetching pricing data:", error);
      }
    };

    const fetchUserPlan = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organization/${orgId}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        const planName = response.data.plan.plan_name;
        const subscriptionId = response.data.plan.subscriptionId;
        setPptCount(response.data.pptcount);
        setPptCountMonthly(response.data.pptcount_monthly);
        if (planName === "pro" || response.data.is_eligible === true) {
          setEligibleForGeneration(true);
        } else {
          setEligibleForGeneration(false);
        }
        dispatch(setUserPlan(planName));
        setSubId(subscriptionId);
      } catch (error) {
        console.error("Error fetching user plan:", error);
      }
    };

    fetchUserPlan();
    getPricingData();
  }, []);
  const monthlyPlanAmount = monthlyPlan?.item.amount! / 100;
  const monthlyPlanId = monthlyPlan?.id;
  const yearlyPlanAmount = yearlyPlan?.item.amount! / 100;
  const yearlyPlanId = yearlyPlan?.id;
  const Tooltip: React.FC<{ message: string }> = ({ message }) => {
    return (
      <div className="absolute left-8 bottom-10 w-72 bg-gray-800 text-white text-sm rounded-lg px-4 py-2 shadow-lg">
        {message}
      </div>
    );
  };
  const [visibleTooltip, setVisibleTooltip] = useState<string | null>(null);
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!isHovered && visibleTooltip === "generate") {
      timeout = setTimeout(() => {
        setVisibleTooltip(null);
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [isHovered, visibleTooltip]);
  const [isOutlineLoading, setIsOutlineLoading] = useState<boolean>(false);
  const [documentName, setDocumentName] = useState<string>("");

  // Fetch outlines when modal opens and document ID is available
  useEffect(() => {
    if (isRefineModalOpen && generatedDocumentIDoutline) {
      fetchOutlines();
    }
  }, [isRefineModalOpen, generatedDocumentIDoutline, fetchOutlines]);

  const generateOutline = async () => {
    setLoading(true);
    setIsOutlineLoading(true);

    let docID = generatedDocumentIDoutline || generateDocumentID();
    setGeneratedDocumentIDoutline(docID);
    const autoSubmitAfterAuth = sessionStorage.getItem("autoSubmitAfterAuth");
    const payload = {
      pptInput: generateInput || "",
      pdfLink: pdfLink || "",
      deviceType: userAgent,
      // Add presentation options to payload
      wordLength: wordLength,
      includeImages: includeImages,
      targetLanguage: targetLanguage,
      presentationTone: presentationTone,
      targetAudience: targetAudience,
    };

    const storedInput = sessionStorage.getItem("pendingGenerateInput");
    const storedPdfLink = sessionStorage.getItem("pendingPdfLink");
    const storedFileName = sessionStorage.getItem("pendingFileName");
    const storedFileType = sessionStorage.getItem("pendingFileType");
    const autoOpenModalAfterAuth = sessionStorage.getItem(
      "autoOpenModalAfterAuth"
    );
    // Retrieve stored presentation options
    const storedWordLength = sessionStorage.getItem("pendingWordLength");
    const storedIncludeImages = sessionStorage.getItem("pendingIncludeImages");
    const storedTargetLanguage = sessionStorage.getItem(
      "pendingTargetLanguage"
    );
    const storedPresentationTone = sessionStorage.getItem(
      "pendingPresentationTone"
    );
    const storedTargetAudience = sessionStorage.getItem(
      "pendingTargetAudience"
    );

    // Retrieve stored inner modal presentation options
    const storedReferenceWebUrl = sessionStorage.getItem(
      "pendingReferenceWebUrl"
    );
    const storedIncludeWebSearch = sessionStorage.getItem(
      "pendingIncludeWebSearch"
    );

    try {
      // New API integration
      const requestUrl = `${API_URL}/api/v1/data/generatedocument/generate-document/outline/${orgId}/${docID}`;

      // Build payload as per new API
      const newPayload = {
        documentInput: generateInput || "",
        tone: presentationTone === "Auto" ? "Professional" : presentationTone,
        audience:
          targetAudience === "Auto" ? "General Audience" : targetAudience,
        language: targetLanguage === "Auto" ? "English" : targetLanguage,
        images: includeImages,
        wordLength:
          wordLength === "Auto" ? "One-Pager (500–1,000 words)" : wordLength,
      };
      // If pdfLink/documentUrl is present, add it
      if (pdfLink) {
        (newPayload as any).pdfLink = pdfLink;
      }

      const response = await axios.post(requestUrl, newPayload, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.status !== 200)
        throw new Error("Failed to generate outline");

      setOutline(response.data.outline);
      setDocumentName(response.data.documentName);

      console.log("Outline generated successfully:", response.data.outline);

      // ✅ Notify other components via Redux (same as SelectPresentationType)
      dispatch(setIsOutlineGenerated(true));

      setIsOutlineLoading(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error("API Error:", error.response?.data || error.message);
      } else {
        console.error("Unexpected Error:", error);
      }
      setIsOutlineLoading(false);
    } finally {
      setLoading(false);
      console.log("Device Type:", userAgent);
      setIsOutlineLoading(false);
    }
  };

  const generateDocument = async () => {
    setLoading(true);
    navigate(`/documentview?documentID=${generatedDocumentIDoutline}`);
    if (!generatedDocumentIDoutline) {
      console.error("Error: Document ID is missing. Generate outline first.");
      setLoading(false);
      return;
    }
    const colorsToUse = brandingColorsSave.length
      ? brandingColorsSave
      : Object.values(organizationColors || {});

    const colorKeys = [
      "P100",
      "P75_S25",
      "P50_S50",
      "P25_S75",
      "S100",
      "F_P100",
      "F_P75_S25",
      "F_P50_S50",
      "F_P25_S75",
      "F_S100",
      "SCL",
      "SCD",
    ];
    const payload = {
      websiteUrl: websiteUrl || "",
      colors: colorKeys.reduce((acc: { [key: string]: string }, key, index) => {
        acc[key] = colorsToUse[index] || "#000000"; // Default to black if missing
        return acc;
      }, {} as { [key: string]: string }),
      creativity: creativity, // Number
      theme: theme, // String
      language: language || "English",
      webSearchToogle: includeWebSearch,
      // Add presentation options to payload
      wordLength: wordLength,
      includeImages: includeImages,
      targetLanguage: targetLanguage,
      presentationTone: presentationTone,
      targetAudience: targetAudience,
    };

    try {
      // Use new API endpoint
      const requestUrl = `${API_URL}/api/v1/data/generatedocument/generate-document/${orgId}/${generatedDocumentIDoutline}`;

      // Build payload for new API
      const newPayload = {
        websiteUrl: websiteUrl || "",
        colors: colorKeys.reduce(
          (acc: { [key: string]: string }, key, index) => {
            acc[key] = colorsToUse[index] || "#000000";
            return acc;
          },
          {} as { [key: string]: string }
        ),
        // {
        //   P100: "#17191A",
        //   P75_S25: "#313B48",
        //   P50_S50: "#4A5C75",
        //   P25_S75: "#647EA3",
        //   S100: "#7D9FD0",
        //   F_P100: "#E8E8E8",
        //   F_P75_S25: "#E4E8EC",
        //   F_P50_S50: "#FFFFFF",
        //   F_P25_S75: "#FFFFFF",
        //   F_S100: "#FFFFFF",
        //   SCL: "#FFFFFF",
        //   SCD: "#000000",
        // },

        creativity: creativity,
        theme: "Light",
        language: language || "English",
        webSearchToogle: includeWebSearch,
        technicaldepth:
          technicalDepth == "Auto" ? "Moderate" : technicalDepth || "Simplify",
      };

      const response = await axios.post(requestUrl, newPayload, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status !== 200)
        throw new Error("Failed to generate document");

      const data = response.data;
      setOutline(data.outline);

      // Call history API after successful document generation
      try {
        const userEmail = sessionStorage.getItem("userEmail");
        const currentTime = new Date().toISOString();

        const historyPayload = {
          UserID: userEmail || "",
          FormID: generatedDocumentIDoutline || "",
          PresentationURL: "wordfile",
          pptName: documentName || "Document",
          currentTime: currentTime,
          paymentStatus: 0,
          exportStatus: false,
          order_id: "default_order",
          ppt_type: "Document",
        };

        await axios.post(
          `${API_URL}/api/v1/data/appscripts/history`,
          historyPayload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
          }
        );

        console.log(
          "History API called successfully after document generation"
        );
      } catch (historyErr) {
        console.error("Failed to call history API:", historyErr);
        // Don't throw error as the document generation was successful
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = async () => {
    try {
      const documentID = await generateDocument(); // Wait for completion
    } catch (error) {
      console.error("Error generating document:", error);
    }
  };

  const fetchOrganizationData = async () => {
    if (!orgId) {
      console.error("Error: Organization ID is missing.");
      return;
    }

    setLoading(true);
    const requestUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organization/${orgId}`;

    try {
      console.log("Fetching Organization Data:", requestUrl);

      const response = await axios.get(requestUrl, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (response.status !== 200) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }

      const data = response.data;
      console.log("Fetched Organization Data:", data);

      setWebsiteUrl(data.websiteLink || "");

      // Ensure colors exist before processing
      if (data.color && typeof data.color === "object") {
        const colorMap = {
          P100: data.color.P100 || "#17191A",
          P75_S25: data.color.P75_S25 || "#313B48",
          P50_S50: data.color.P50_S50 || "#4A5C75",
          P25_S75: data.color.P25_S75 || "#647EA3",
          S100: data.color.S100 || "#7D9FD0",
          F_P100: data.color.F_P100 || "#E8E8E8",
          F_P75_S25: data.color.F_P75_S25 || "#E4E8EC",
          F_P50_S50: data.color.F_P50_S50 || "#FFFFFF",
          F_P25_S75: data.color.F_P25_S75 || "#FFFFFF",
          F_S100: data.color.F_S100 || "#FFFFFF",
          SCL: data.color.SCL || "#FFFFFF",
          SCD: data.color.SCD || "#000000",
        };

        setBrandingColors([
          colorMap.P100,
          colorMap.P75_S25,
          colorMap.P50_S50,
          colorMap.P25_S75,
          colorMap.S100,
        ]);

        setOrganizationColors(colorMap);
        setPrimaryColor(colorMap.P100);
        setTextColor(colorMap.F_P100);
        setSecondaryColor(colorMap.S100);
      }
    } catch (error) {
      if (error instanceof Error) {
        console.error("Error fetching organization data:", error.message);
      } else {
        console.error("Error fetching organization data:", error);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchOrganizationData();

    // Check if there's pending data but the user isn't logged in yet
    const hasPendingData =
      sessionStorage.getItem("pendingGenerateInput") ||
      sessionStorage.getItem("pendingPdfLink");
    if (!authToken && hasPendingData) {
      // Store flag to open modal after login
      sessionStorage.setItem("autoOpenModalAfterAuth", "true");
      toast.info("Please log in to continue. Your data will be preserved.", {
        position: "top-right",
        autoClose: 2000,
        onClose: () => {
          window.location.href = "/auth";
        },
      });
    }
  }, [orgId, authToken]);
  // Check for stored data after login
  useEffect(() => {
    const storedInput = sessionStorage.getItem("pendingGenerateInput");
    const storedPdfLink = sessionStorage.getItem("pendingPdfLink");
    const storedFileName = sessionStorage.getItem("pendingFileName");
    const storedFileType = sessionStorage.getItem("pendingFileType");
    const autoSubmitAfterAuth = sessionStorage.getItem("autoSubmitAfterAuth");
    const autoOpenModalAfterAuth = sessionStorage.getItem(
      "autoOpenModalAfterAuth"
    );
    // Retrieve stored presentation options
    const storedWordLength = sessionStorage.getItem("pendingWordLength");
    const storedIncludeImages = sessionStorage.getItem("pendingIncludeImages");
    const storedTargetLanguage = sessionStorage.getItem(
      "pendingTargetLanguage"
    );
    const storedPresentationTone = sessionStorage.getItem(
      "pendingPresentationTone"
    );
    const storedTargetAudience = sessionStorage.getItem(
      "pendingTargetAudience"
    );

    // Retrieve stored inner modal presentation options
    const storedReferenceWebUrl = sessionStorage.getItem(
      "pendingReferenceWebUrl"
    );
    const storedIncludeWebSearch = sessionStorage.getItem(
      "pendingIncludeWebSearch"
    );
    const storedTechnicalDepth = sessionStorage.getItem(
      "pendingTechnicalDepth"
    );
    const storedIncludeCitations = sessionStorage.getItem(
      "pendingIncludeCitations"
    );

    // If we have auth token and stored data, it means user has just logged in
    if (authToken && (storedInput || storedPdfLink)) {
      // Restore all prefill data
      if (storedInput) setGenerateInput(storedInput);
      if (storedPdfLink) {
        setPdfLink(storedPdfLink);
        if (storedFileName && storedFileType) {
          const placeholderFile = new File([""], storedFileName, {
            type: storedFileType,
          });
          setFile(placeholderFile);
        }
      }
      if (storedWordLength) setWordLength(storedWordLength);
      if (storedIncludeImages) setIncludeImages(storedIncludeImages === "true");
      if (storedTargetLanguage) setTargetLanguage(storedTargetLanguage);
      if (storedPresentationTone) setPresentationTone(storedPresentationTone);
      if (storedTargetAudience) setTargetAudience(storedTargetAudience);
      if (storedReferenceWebUrl) setReferenceWebUrl(storedReferenceWebUrl);
      if (storedIncludeWebSearch)
        setIncludeWebSearch(storedIncludeWebSearch === "true");
      // if (storedTechnicalDepth) setTechnicalDepth(storedTechnicalDepth);
      if (storedIncludeCitations)
        setIncludeCitations(storedIncludeCitations === "true");

      // Clear stored data after using it
      sessionStorage.removeItem("pendingGenerateInput");
      sessionStorage.removeItem("pendingPdfLink");
      sessionStorage.removeItem("pendingFileName");
      sessionStorage.removeItem("pendingFileType");
      sessionStorage.removeItem("autoSubmitAfterAuth");
      sessionStorage.removeItem("pendingWordLength");
      sessionStorage.removeItem("pendingIncludeImages");
      sessionStorage.removeItem("pendingTargetLanguage");
      sessionStorage.removeItem("pendingPresentationTone");
      sessionStorage.removeItem("pendingTargetAudience");
      sessionStorage.removeItem("pendingReferenceWebUrl");
      sessionStorage.removeItem("pendingIncludeWebSearch");
      sessionStorage.removeItem("pendingTechnicalDepth");
      sessionStorage.removeItem("pendingIncludeCitations");

      // If autoOpenModalAfterAuth flag was set, open the modal automatically
      // if (autoOpenModalAfterAuth === "true") {
      //   setIsRefineModalOpen(true);

      // }

      // Show success message after login but don't auto-submit
      if (autoSubmitAfterAuth === "true") {
        toast.success("Successfully logged in! Your data has been restored.", {
          position: "top-right",
          autoClose: 3000,
        });
      }

      sessionStorage.removeItem("autoOpenModalAfterAuth");
    }
  }, [authToken]);

  const [showCreativityTooltip, setShowCreativityTooltip] = useState(false);
  const [displayedTitle, setDisplayedTitle] = useState("");
  const fullTitle =
    "Write reports, proposals, essays, and business documents with AI.";

  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index < fullTitle.length) {
        setDisplayedTitle(fullTitle.slice(0, index + 1));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 50);

    return () => clearInterval(timer);
  }, []);

  const isLoggedIn = !!authToken;

  return (
    <div
      className={`relative flex p-2 flex-col items-center justify-start pt-2 min-h-[80vh]  sm:min-h-[85vh]   md:min-h-[90vh]    xl:min-h-screen w-full ${!isLoggedIn ? 'pb-32 sm:pb-40' : ''}`}
      style={{
        backgroundColor: Theme.colors.background,
      }}
    >
      <div
        className={`relative w-full text-center flex flex-col items-center justify-start h-[80vh]    xl:h-[60vh]   ${
          isLoggedIn ? "" : ""
        }`}
        // style={{
        //   minHeight: "100vh",
        // }}
      >
        {/* Dot Pattern Background */}
        <div
          className="absolute inset-0 w-full h-full opacity-50"
          style={{
            backgroundImage: `radial-gradient(circle, #d1d5db 1px, transparent 1px)`,
            backgroundSize: "20px 20px",
            backgroundPosition: "0 0, 10px 10px",
          }}
        />
        <div className="absolute inset-0 z-[-1] rounded-3xl"></div>

        {/* Title */}
        <h2
          className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-2 md:mb-4 leading-tight text-gray-90 ${
            isLoggedIn ? "mt-2 md:mt-4 lg:mt-6 2xl:mt-10" : ""
          }`}
        >
          {displayedTitle}
          <span className="cursor-pulse">|</span>
        </h2>

        {/* <h2
    className="text-2xl font-semibold mb-6 text-gray-700"
    style={{ color: Theme.colors.text.strong }}
  >
    Tell us what you want to build
  </h2> */}

        <p
          className="flex items-center gap-1 relative text-base md:text-lg mb-3 md:mb-4 mt-2 md:mt-3 lg:mt-6 xl:mt-8"
          style={{ color: Theme.colors.text.weak }}
        >
          Enter your document context.
          <span
            className="cursor-pointer relative ml-2"
            style={{ color: Theme.colors.primary }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <FaInfoCircle className="cursor-pointer relative" />

            {/* Desktop tooltip */}
            {showTooltip && (
              <div
                className="absolute z-50 hidden lg:block left-full ml-2 transform -translate-y-1/2 w-72 p-4 text-xs rounded-2xl shadow-lg opacity-100 transition-opacity duration-300 text-left"
                style={{
                  backgroundColor: Theme.colors.grounding,
                  color: Theme.colors.background,
                }}
              >
                <p className="font-semibold mb-2">
                  Provide clear details about your presentation requirements.
                  The more specific you are, the more relevant the output will
                  be.
                </p>
                <p>
                  - Outline the objective and key message of the presentation.
                </p>
                <p>
                  - Specify any required slides, information, or data to
                  include.
                </p>
                <p>
                  - Provide detailed instructions to ensure alignment with your
                  vision.
                </p>
              </div>
            )}

            {/* Mobile tooltip */}
            {showTooltip && (
              <div
                className="absolute z-50 top-0 lg:hidden left-[80%] -translate-x-[80%] -translate-y-[80%] w-72 p-4 text-xs rounded-2xl shadow-lg opacity-100 transition-opacity duration-300 text-left"
                style={{
                  backgroundColor: Theme.colors.grounding,
                  color: Theme.colors.background,
                }}
              >
                <p className="font-semibold mb-2">
                  Provide clear details about your presentation requirements.
                  The more specific you are, the more relevant the output will
                  be.
                </p>
                <p>
                  - Outline the objective and key message of the presentation.
                </p>
                <p>
                  - Specify any required slides, information, or data to
                  include.
                </p>
                <p>
                  - Provide detailed instructions to ensure alignment with your
                  vision.
                </p>
              </div>
            )}
          </span>
        </p>

        {/* Textarea card with toolbar */}
        <div className="relative  w-full flex justify-center">
          <div
            className="lg:w-2/3 w-[92%] mx-auto rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition"
            style={{
              border: `1px solid ${Theme.colors.stroke.weak}`,
              backgroundColor: Theme.colors.background,
            }}
          >
            <textarea
              className="w-full h-[7.5rem] p-6 resize-none bg-transparent outline-none text-gray-800"
              style={{
                fontFamily: Theme.font.family,
              }}
              placeholder="Add text or drop content here — more details mean a more tailored document!"
              value={generateInput || ""}
              onChange={(e) => setGenerateInput(e.target.value)}
              minLength={file ? 0 : 100}
              maxLength={10000}
            />

            {/* Toolbar */}
            <div
              className="flex flex-col min-[380px]:flex-row min-[380px]:items-center min-[380px]:justify-between px-5 py-3 bg-gray-50 gap-3 min-[380px]:gap-0"
              style={{ borderTop: `1px solid ${Theme.colors.stroke.weak}` }}
            >
              <div className="flex items-center gap-3 min-[380px]:gap-6 flex-wrap min-[380px]:flex-nowrap">
                <label className="cursor-pointer flex items-center gap-1 min-[380px]:gap-2 text-sm min-[380px]:text-base font-medium text-blue-600 hover:text-blue-700 min-w-0">
                  {pdfUploading ? (
                    <span className="text-gray-700 text-sm">Uploading...</span>
                  ) : file ? (
                    <>
                      <button
                        type="button"
                        className="text-gray-500 hover:text-red-500 flex-shrink-0"
                        onClick={(e) => {
                          e.preventDefault();
                          setFile(null);
                          setPdfLink("");
                        }}
                      >
                        <FaTimes className="text-sm" />
                      </button>
                      <span className="truncate max-w-[80px] min-[380px]:max-w-[120px] sm:max-w-[150px] md:max-w-[200px] text-sm min-[380px]:text-base">
                        {file.name}
                      </span>
                    </>
                  ) : (
                    <>
                      <FaPaperclip className="text-sm min-[380px]:text-lg flex-shrink-0" />
                      <span className="text-sm min-[380px]:text-base whitespace-nowrap">Upload Document</span>
                      <input
                        type="file"
                        accept=".pdf, .doc, .docx, .ppt, .pptx"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </>
                  )}
                </label>

                {/* Images toggle */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-sm min-[380px]:text-base font-medium text-gray-600">
                    Images
                  </span>
                  <button
                    type="button"
                    onClick={() => setIncludeImages(!includeImages)}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                    style={{
                      backgroundColor: includeImages
                        ? Theme.colors.primary
                        : Theme.colors.stroke.weak,
                    }}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        includeImages ? "translate-x-5" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div
                className="text-sm font-medium text-center min-[380px]:text-left min-[380px]:self-auto"
                style={{
                  color:
                    !file && generateInput.length < 100
                      ? "red"
                      : Theme.colors.text.strong,
                }}
              >
                {generateInput.length}
              </div>
            </div>
          </div>
        </div>

        {/* Dropdowns */}
        <div className="w-[92%] lg:w-2/3 mt-3 md:mt-4 lg:mt-6 mb-3 md:mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
            {[
              {
                label: "Word Length",
                value: wordLength,
                setter: setWordLength,
              },
              {
                label: "Target Language",
                value: targetLanguage,
                setter: setTargetLanguage,
              },
              {
                label: "Document Tone",
                value: presentationTone,
                setter: setPresentationTone,
              },
              {
                label: "Target Audience",
                value: targetAudience,
                setter: setTargetAudience,
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="relative z-10 p-4 rounded-2xl border shadow-sm hover:shadow-md transition bg-gray-50"
                style={{
                  borderColor: Theme.colors.stroke.weak,
                  backgroundColor: "#f9fafb",
                }}
              >
                <label className="block text-sm font-medium mb-2 text-gray-600">
                  {item.label}
                </label>

                <select
                  value={item.value}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (item.label === "Word Length") setWordLength(v);
                    else if (item.label === "Target Language")
                      setTargetLanguage(v);
                    else if (item.label === "Document Tone")
                      setPresentationTone(v);
                    else if (item.label === "Target Audience")
                      setTargetAudience(v);
                  }}
                  className="w-full p-2 rounded-xl focus:ring-2 focus:ring-blue-500 border border-gray-200 text-gray-800 focus:outline-none"
                >
                  {item.label === "Word Length" ? (
                    <>
                      <option value="Auto">Auto</option>
                      <optgroup
                        label="Short-Form (Quick Reads)"
                        className="font-semibold"
                      >
                        <option value="Summary (under 300 words)">
                          Summary (under 300 words)
                        </option>
                        <option value="Brief Note (300–500 words)">
                          Brief Note (300–500 words)
                        </option>
                      </optgroup>

                      <optgroup
                        label="Medium-Form (Standard Business Docs)"
                        className="font-semibold"
                      >
                        <option value=" One-Pager (500–1,000 words)">
                          One-Pager (500–1,000 words)
                        </option>
                        <option value=" Standard Document (1,000–1,500 words)">
                          Standard Document (1,000–1,500 words)
                        </option>
                      </optgroup>

                      <optgroup
                        label="Long-Form (In-Depth Docs)"
                        className="font-semibold"
                      >
                        <option value="Detailed Report (1,500–2,500+ words)">
                          Detailed Report (1,500–2,500+ words)
                        </option>
                      </optgroup>
                    </>
                  ) : item.label === "Target Language" ? (
                    <>
                      <option value="Auto">Auto</option>
                      {supportedLanguages.map((lang) => (
                        <option key={lang} value={lang}>
                          {lang}
                        </option>
                      ))}
                    </>
                  ) : item.label === "Document Tone" ? (
                    <>
                      <option value="Auto">Auto</option>
                      <option value="Professional">Professional</option>
                      <option value="Casual">Casual</option>
                      <option value="Formal">Formal</option>
                      <option value="Conversational">Conversational</option>
                      <option value="Technical">Technical</option>
                      <option value="Persuasive">Persuasive</option>
                    </>
                  ) : (
                    <>
                      <option value="Auto">Auto</option>
                      <option value="Business professionals">
                        Business professionals
                      </option>
                      <option value="Technical experts">
                        Technical experts
                      </option>
                      <option value="General audience">General audience</option>
                      <option value="Executives">Executives</option>
                      <option value="Investors">Investors</option>
                      <option value="Clients">Clients</option>
                      <option value="Students">Students</option>
                    </>
                  )}
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Submit button */}
        <div
          className="flex justify-center mt-3 md:mt-4 lg:mt-6 xl:mt-8 2xl:mt-12 relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => {
            setTimeout(() => setIsHovered(false), 3000);
          }}
        >
          <button
            id="generate-presentation"
            className={`h-[2.8rem] md:h-[3.1rem] px-8 md:px-16 py-3 md:py-4 rounded-full font-semibold text-sm md:text-base 
  active:scale-95 transition transform duration-300 
  flex items-center justify-center ${
    (!file && !generateInput) ||
    (!file && generateInput && generateInput.length < 100) ||
    pdfUploading
      ? "cursor-not-allowed"
      : "hover:opacity-90"
  }`}
            style={{
              backgroundColor:
                (!file && !generateInput) ||
                (!file && generateInput && generateInput.length < 100) ||
                pdfUploading
                  ? Theme.colors.stroke.strong
                  : Theme.colors.primary,
              color: "#FFFFFF",
              cursor:
                (!file && !generateInput) ||
                pdfUploading ||
                (!file && generateInput && generateInput.length < 100)
                  ? "not-allowed"
                  : "pointer",
            }}
            onClick={() => {
              // Check if user is authenticated
              if (!authToken) {
                // User is not logged in, store the input data in sessionStorage
                if (generateInput) {
                  sessionStorage.setItem("pendingGenerateInput", generateInput);
                  console.log("Stored input in sessionStorage:", generateInput);
                }

                if (file && pdfLink) {
                  sessionStorage.setItem("pendingPdfLink", pdfLink);
                  sessionStorage.setItem("pendingFileName", file.name);
                  sessionStorage.setItem("pendingFileType", file.type);
                  console.log("Stored file info in sessionStorage:", {
                    pdfLink,
                    fileName: file.name,
                    fileType: file.type,
                  });
                }

                // Store presentation options in sessionStorage
                sessionStorage.setItem("pendingWordLength", wordLength);
                sessionStorage.setItem(
                  "pendingIncludeImages",
                  includeImages.toString()
                );
                sessionStorage.setItem("pendingTargetLanguage", targetLanguage);
                sessionStorage.setItem(
                  "pendingPresentationTone",
                  presentationTone
                );
                sessionStorage.setItem("pendingTargetAudience", targetAudience);

                // Store new inner modal presentation options
                sessionStorage.setItem(
                  "pendingReferenceWebUrl",
                  referenceWebUrl
                );
                sessionStorage.setItem(
                  "pendingIncludeWebSearch",
                  includeWebSearch.toString()
                );
                sessionStorage.setItem("pendingTechnicalDepth", technicalDepth);
                sessionStorage.setItem(
                  "pendingIncludeCitations",
                  includeCitations.toString()
                );

                console.log("Stored presentation options in sessionStorage", {
                  includeImages,
                  wordLength,
                  targetLanguage,
                  presentationTone,
                  targetAudience,
                  referenceWebUrl,
                  includeWebSearch,
                  technicalDepth,
                  includeCitations,
                });

                // Set flag to auto-submit after authentication
                sessionStorage.setItem("autoSubmitAfterAuth", "true");
                sessionStorage.setItem("autoOpenModalAfterAuth", "true");
                console.log(
                  "Auth flow initiated, data stored in sessionStorage"
                );

                // Redirect directly to auth page without toast
                window.location.href = `/auth`;
                sessionStorage.setItem("redirectAfterAuth", "/new-document");
                return;
              }

              // Proceed with normal flow if authenticated
              setIsRefineModalOpen(true);
              generateOutline();
              updatePptCount();
            }}
            disabled={
              (!file && !generateInput) ||
              (!file && generateInput && generateInput.length < 100) ||
              pdfUploading
            }
          >
            Submit
          </button>

          {isHovered && !file && generateInput.length < 100 && (
            <div className="absolute top-full mt-2 w-[14rem] p-3 rounded-2xl shadow-md opacity-100 transition-opacity duration-300 text-center bg-gray-100 text-gray-600">
              Please provide minimum 100 characters in presentation context or
              Upload a document.
            </div>
          )}
        </div>
      </div>

      {/* Pricing modal removed — redirect to external pricing page instead */}
      

      {/* Refine Modal */}
      {isRefineModalOpen && (
        <div
          className="fixed inset-0 z-30 flex justify-center items-center"
          style={{ backgroundColor: "rgba(23, 25, 26, 0.3)" }}
        >
          <div
            className="relative w-[90%] sm:w-[80%] h-[90%] xl:h-[80%] 2xl:h-[75%] max-w-6xl bg-white rounded-2xl shadow-xl p-2 xs:p-6 flex flex-col"
            style={{ backgroundColor: Theme.colors.background }}
          >
            {/* Close Button */}
            <button
              title="CloseModal"
              onClick={handleCloseModal}
              className="absolute top-4 right-4 p-2 bg-gray-200 rounded-full z-10"
            >
              <FaTimes style={{ color: Theme.colors.text.weak }} />
            </button>

            {/* Header with Tabs and Generate Button */}
            <div className="flex w-full items-center justify-between border-b border-gray-300 mb-4">
              {/* Tabs Container */}
              <div className="flex items-center">
                <button
                  onClick={() => setActiveTab("outline")}
                  className={`py-2 px-4 font-semibold transition-all ${
                    activeTab === "outline"
                      ? "border-b-4 border-blue-600 text-blue-600"
                      : "text-gray-500 hover:text-blue-600"
                  }`}
                >
                  Document Outline
                </button>
                <button
                  onClick={() => setActiveTab("options")}
                  className={`py-2 px-4 font-semibold transition-all ${
                    activeTab === "options"
                      ? "border-b-4 border-blue-600 text-blue-600"
                      : "text-gray-500 hover:text-blue-600"
                  }`}
                >
                  Document Options
                </button>
              </div>
            </div>

            <div className="w-full h-full overflow-y-auto scrollbar-none mt-2">
              {activeTab === "outline" ? (
                <div className="space-y-4  pr-2 max-h-full">
                  {isOutlineLoading ? (
                    <div className="flex flex-col justify-center items-center py-10">
                      <ul className="space-y-6 w-full">
                        {Array.from({ length: 5 }).map((_, idx) => (
                          <li key={idx} className="relative w-full">
                            <div className="border w-full border-gray-200 p-4 rounded-md shadow-sm bg-white flex flex-col gap-2 animate-pulse">
                              <div className="h-5 bg-gray-200 rounded w-2/3"></div>
                              <div className="h-4 bg-gray-100 rounded w-1/3"></div>
                            </div>
                          </li>
                        ))}
                      </ul>
                      {/* <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-base">Outline Generating</p> */}
                    </div>
                  ) : (
                    <div className="flex flex-col max-h-[70vh]">
                      <div className="flex-1 overflow-y-auto pr-1">
                        <ul className="space-y-6">
                          {outline?.map((slide: any, idx: number) => (
                            <React.Fragment key={slide.outlineID}>
                              <li className="relative group">
                                <div className="relative border border-gray-200 p-4 rounded-md shadow-sm bg-white flex flex-col gap-2 transition-all duration-200 hover:shadow-md hover:border-gray-300">
                                  {editingIndex !== idx && (
                                    <div className="absolute top-2 right-2 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                      <button
                                        onClick={() => {
                                          setEditingIndex(idx);
                                          setEditedOutline({
                                            title: slide.title,
                                            keywords: slide.keywords || "",
                                          });
                                        }}
                                        title="Edit"
                                        className="transition-all duration-200 hover:scale-110"
                                      >
                                        <FaEdit
                                          className="text-gray-500 hover:text-blue-600 transition-colors duration-200"
                                          style={{ fontSize: "1rem" }}
                                        />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteOutline(idx)}
                                        title="Delete"
                                        className="transition-all duration-200 hover:scale-110"
                                      >
                                        <FaTrash
                                          className="text-gray-500 hover:text-red-500 transition-colors duration-200"
                                          style={{ fontSize: "1rem" }}
                                        />
                                      </button>
                                    </div>
                                  )}

                                  {editingIndex === idx ? (
                                    <div className="flex flex-col gap-3">
                                      <input
                                        value={editedOutline.title}
                                        onChange={(e) =>
                                          setEditedOutline({
                                            ...editedOutline,
                                            title: e.target.value,
                                          })
                                        }
                                        placeholder="Title"
                                        className="p-2 border border-gray-300 rounded-md transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                      />
                                      <input
                                        value={editedOutline.keywords}
                                        onChange={(e) =>
                                          setEditedOutline({
                                            ...editedOutline,
                                            keywords: e.target.value,
                                          })
                                        }
                                        placeholder="Keywords"
                                        className="p-2 border border-gray-300 rounded-md transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                      />
                                      <div className="flex gap-3">
                                        <button
                                          className="text-white bg-blue-600 px-4 py-2 rounded-md hover:bg-blue-700 transition-all duration-200"
                                          onClick={async () => {
                                            if (
                                              editedOutline.title ===
                                                slide.title &&
                                              editedOutline.keywords ===
                                                (slide.keywords || "")
                                            ) {
                                              setEditingIndex(null);
                                              return;
                                            }

                                            const updated = [...outline];
                                            updated[idx] = {
                                              ...slide,
                                              title: editedOutline.title,
                                              keywords: editedOutline.keywords,
                                            };
                                            setOutline(updated);
                                            setEditingIndex(null);

                                            try {
                                              await axios.post(
                                                `${process.env.REACT_APP_BACKEND_URL}/api/v1/outline/blocklist/update`,
                                                {
                                                  documentId:
                                                    generatedDocumentIDoutline,
                                                  blockItem: {
                                                    outlineID: slide.outlineID,
                                                    title: editedOutline.title,
                                                    keywords:
                                                      editedOutline.keywords,
                                                  },
                                                },
                                                {
                                                  headers: {
                                                    Authorization: `Bearer ${authToken}`,
                                                  },
                                                }
                                              );

                                              // Notify document view component to refresh outlines
                                              dispatch(
                                                setIsOutlineGenerated(true)
                                              );
                                            } catch (error) {
                                              console.error(
                                                "Update failed",
                                                error
                                              );
                                            }
                                          }}
                                        >
                                          Save
                                        </button>
                                        <button
                                          className="text-gray-700 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-100 transition-all duration-200"
                                          onClick={() => setEditingIndex(null)}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <>
                                      <h3 className="text-lg font-semibold text-gray-800">
                                        {slide.title}
                                      </h3>
                                      {slide.keywords && (
                                        <p className="text-sm text-gray-600">
                                          <span className="font-medium">
                                            Keywords:
                                          </span>{" "}
                                          {slide.keywords}
                                        </p>
                                      )}
                                    </>
                                  )}
                                </div>

                                {idx > -1 && (
                                  <div className="mt-4 flex items-center justify-center space-x-4 relative hidden group-hover:flex">
                                    <div className="flex-grow h-px bg-gray-300"></div>

                                    <div className="relative">
                                      <button
                                        className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-400 bg-white text-gray-500 hover:bg-gray-100 hover:border-gray-500 transition-all duration-200 hover:scale-110"
                                        onClick={() => setInputIndex(idx)}
                                        title="Add new section"
                                      >
                                        +
                                      </button>
                                    </div>

                                    <div className="flex-grow h-px bg-gray-300"></div>
                                  </div>
                                )}
                              </li>

                              {inputIndex === idx && (
                                <div className="border border-gray-200 p-4 rounded-md shadow-sm bg-white flex flex-col gap-3 -mt-2">
                                  <input
                                    type="text"
                                    value={newOutline}
                                    onChange={(e) =>
                                      setNewOutline(e.target.value)
                                    }
                                    placeholder="Title"
                                    className="p-2 border border-gray-300 rounded-md transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                    autoFocus
                                  />
                                  <input
                                    type="text"
                                    value={newOutlineKeywords}
                                    onChange={(e) =>
                                      setNewOutlineKeywords(e.target.value)
                                    }
                                    placeholder="Keywords"
                                    className="p-2 border border-gray-300 rounded-md transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                                  />

                                  <div className="flex gap-3">
                                    <button
                                      className="text-white bg-blue-600 px-4 py-2 rounded-md hover:bg-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                      onClick={() => handleAddOutline(idx)}
                                      disabled={!newOutline.trim()}
                                    >
                                      {newOutlineLoading ? (
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      ) : (
                                        "Add"
                                      )}
                                    </button>
                                    <button
                                      className="text-gray-700 border border-gray-300 px-4 py-2 rounded-md hover:bg-gray-100 transition-all duration-200"
                                      onClick={() => {
                                        setInputIndex(null);
                                        setNewOutline("");
                                        setNewOutlineKeywords("");
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </ul>
                      </div>
                      <div className="flex w-full justify-center mt-4">
                        <button
                          disabled={
                            isLoading || (websiteUrl.length > 0 && !isValidLink)
                          }
                          onClick={handleButtonClick}
                          className={`lg:w-1/2 w-[80%] py-2 rounded-lg font-semibold active:scale-95 transition transform duration-300 ${
                            isLoading || (websiteUrl.length > 0 && !isValidLink)
                              ? "cursor-not-allowed"
                              : "hover:opacity-90"
                          }`}
                          style={{
                            backgroundColor:
                              isLoading ||
                              (websiteUrl.length > 0 && !isValidLink)
                                ? Theme.colors.stroke.weak
                                : Theme.colors.primary,
                            color:
                              isLoading ||
                              (websiteUrl.length > 0 && !isValidLink)
                                ? Theme.colors.text.weak
                                : "#FFFFFF",
                          }}
                        >
                          Generate Document
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="flex flex-col h-full">
                    {/* Scrollable Form Content */}
                    <div className="flex-1 overflow-y-auto space-y-6 px-1 pr-2">
                      {/* Reference Web URL + Web Search + Citations in single row */}
                      <div className="flex flex-col lg:flex-row justify-between gap-4">
                        {/* Reference Web URL */}
                        <div className="space-y-2 lg:flex-1 lg:max-w-md">
                          <label
                            className="font-semibold flex items-center gap-2 text-sm"
                            style={{ color: Theme.colors.text.strong }}
                          >
                            Reference Web URL
                            <span
                              className="cursor-pointer relative"
                              style={{ color: Theme.colors.primary }}
                              onMouseEnter={() => {
                                setShowTooltip3(true);
                              }}
                              onMouseLeave={() => {
                                setShowTooltip3(false);
                              }}
                            >
                              <FaInfoCircle className="text-sm" />
                              {showTooltip3 && (
                                <div
                                  className="absolute text-left top-[7%] left-[90%] w-72 p-3 text-xs rounded-lg shadow-lg transform -translate-x-1/2 transition-opacity duration-300 opacity-100 z-50"
                                  style={{
                                    backgroundColor: Theme.colors.grounding,
                                    color: Theme.colors.background,
                                    zIndex: 9999,
                                  }}
                                >
                                  <p className="font-semibold">
                                    Why add a reference URL?
                                  </p>
                                  <p>
                                    Provide a web URL from which you would like
                                    us to include information for the document.
                                  </p>
                                </div>
                              )}
                            </span>
                          </label>

                          <input
                            type="text"
                            value={websiteUrl}
                            onChange={(e) => {
                              const updatedValue = e.target.value;
                              if (updatedValue === "") {
                                setWebsiteUrl("");
                                setIsValidLink(true);
                                return;
                              }
                              setWebsiteUrl(updatedValue);
                              const urlRegex =
                                /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
                              setIsValidLink(urlRegex.test(updatedValue));
                            }}
                            onFocus={() => {
                              if (!websiteUrl) setWebsiteUrl("https://");
                            }}
                            className={`w-full p-2 border rounded-lg shadow-sm outline-none focus:ring-2 ${
                              websiteUrl.length > 0 && !isValidLink
                                ? "border-red-500 focus:ring-red-500"
                                : "border-gray-300 focus:ring-[#3667B2]"
                            }`}
                            placeholder="Enter website URL"
                          />
                          {websiteUrl.length > 0 && !isValidLink && (
                            <p className="text-red-500 text-xs">
                              Please enter a valid website link.
                            </p>
                          )}
                        </div>

                        {/* Include Web Search Toggle */}
                        <div className="space-y-2">
                          <label className="font-semibold flex items-center gap-2 text-sm">
                            Include Web Search
                            <span
                              className="cursor-pointer relative"
                              style={{ color: Theme.colors.primary }}
                              onMouseEnter={() => {
                                setShowTooltip5(true);
                              }}
                              onMouseLeave={() => {
                                setShowTooltip5(false);
                              }}
                            >
                              <FaInfoCircle className="text-sm" />
                              {showTooltip5 && (
                                <div
                                  className="absolute text-left top-[7%] right-[5%] w-72 p-3 text-xs rounded-lg shadow-lg transform -translate-x-1/2 transition-opacity duration-300 opacity-100 z-50"
                                  style={{
                                    backgroundColor: Theme.colors.grounding,
                                    color: Theme.colors.background,
                                    zIndex: 9999,
                                  }}
                                >
                                  <p className="font-semibold">
                                    What is Include Web Search?
                                  </p>
                                  <p>
                                    Enable to include relevant web search
                                    results on your document topic in the
                                    generated content.
                                  </p>
                                </div>
                              )}
                            </span>
                          </label>
                          <div className="flex items-center pt-2">
                            <button
                              type="button"
                              onClick={() =>
                                setIncludeWebSearch(!includeWebSearch)
                              }
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#3667B2] focus:ring-offset-2 ${
                                includeWebSearch
                                  ? "bg-[#3667B2]"
                                  : "bg-gray-200"
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  includeWebSearch
                                    ? "translate-x-6"
                                    : "translate-x-1"
                                }`}
                              />
                            </button>
                            <span className="ml-3 text-sm text-gray-600">
                              {includeWebSearch ? "On" : "Off"}
                            </span>
                          </div>
                        </div>

                        {/* Include Citations Toggle */}
                        <div className="space-y-2">
                          <label className="font-semibold flex items-center gap-2 text-sm">
                            Include Citations
                            <span
                              className="cursor-pointer relative"
                              style={{ color: Theme.colors.primary }}
                              onMouseEnter={() => setShowCitationsTooltip(true)}
                              onMouseLeave={() =>
                                setShowCitationsTooltip(false)
                              }
                            >
                              <FaInfoCircle className="text-sm" />
                              {showCitationsTooltip && (
                                <div
                                  className="absolute text-left top-[7%] right-[5%] w-72 p-3 text-xs rounded-lg shadow-lg transform -translate-x-1/2 transition-opacity duration-300 opacity-100 z-50"
                                  style={{
                                    backgroundColor: Theme.colors.grounding,
                                    color: Theme.colors.background,
                                    zIndex: 9999,
                                  }}
                                >
                                  <p className="font-semibold">Citations</p>
                                  <p>
                                    {!includeWebSearch
                                      ? "Citations can only be included or excluded if the web search option is enabled"
                                      : "Include citations and references in your document for better credibility."}
                                    {/* Include citations and references in your
                                    document for better credibility. */}
                                  </p>
                                </div>
                              )}
                            </span>
                          </label>
                          <div className="flex items-center pt-2">
                            <button
                              type="button"
                              onClick={() => {
                                if (includeWebSearch) {
                                  setIncludeCitations(!includeCitations);
                                }
                              }}
                              disabled={!includeWebSearch}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#3667B2] focus:ring-offset-2 ${
                                includeCitations && includeWebSearch
                                  ? "bg-[#3667B2]"
                                  : "bg-gray-200"
                              } ${
                                !includeWebSearch
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  includeCitations && includeWebSearch
                                    ? "translate-x-6"
                                    : "translate-x-1"
                                }`}
                              />
                            </button>
                            <span
                              className={`ml-3 text-sm ${
                                !includeWebSearch
                                  ? "text-gray-400"
                                  : "text-gray-600"
                              }`}
                            >
                              {includeCitations && includeWebSearch
                                ? "On"
                                : "Off"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-6">
                        {/* Branding Colors */}
                        <div>
                          <div className="flex items-center relative">
                            <label
                              className="font-semibold"
                              style={{ color: Theme.colors.text.strong }}
                            >
                              Branding Colors
                            </label>
                            <div
                              className="relative flex items-center ml-2"
                              onMouseEnter={() => setShowTooltip4(true)}
                              onMouseLeave={() => setShowTooltip4(false)}
                            >
                              <FaInfoCircle
                                className="cursor-pointer"
                                style={{ color: Theme.colors.primary }}
                              />
                              {showTooltip4 && (
                                <div
                                  className="hidden lg:absolute lg:block lg:text-left lg:left-1/2 lg:bottom-8 lg:w-72 lg:p-3 text-xs rounded-lg shadow-lg lg:transform lg:-translate-x-1/2 transition-opacity duration-300 lg:opacity-100"
                                  style={{
                                    backgroundColor: Theme.colors.grounding,
                                    color: Theme.colors.background,
                                  }}
                                >
                                  <p className="font-semibold">
                                    Branding Colors
                                  </p>
                                  <p>
                                    These are the colors that will be used in
                                    your presentation. If you've uploaded a
                                    logo, the colors have been extracted from
                                    it.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex justify-between items-center gap-2 lg:gap-0 mt-4 w-full">
                            <div
                              className="flex w-2/3 h-10 rounded-lg overflow-hidden border-2 shadow-md"
                              style={{
                                borderColor: Theme.colors.stroke.weak,
                              }}
                            >
                              {isLoading ? (
                                <p
                                  className="flex items-center justify-center w-full"
                                  style={{ color: Theme.colors.text.weak }}
                                >
                                  Loading colors...
                                </p>
                              ) : (
                                (brandingColors && brandingColors.length > 0
                                  ? brandingColors
                                  : [
                                      "#17191A",
                                      "#313B48",
                                      "#4A5C75",
                                      "#647EA3",
                                      "#7D9FD0",
                                    ]
                                ).map((color, index) => (
                                  <div
                                    key={index}
                                    className="h-full flex-1"
                                    style={{ backgroundColor: color }}
                                  />
                                ))
                              )}
                            </div>

                            <button
                              className={`lg:h-[2.5rem] text-xs font-medium px-4 py-2 rounded-md active:scale-95 transition transform duration-300 ${
                                isLoading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
                              style={{
                                backgroundColor: Theme.colors.background,
                                color: Theme.colors.primary,
                                border: `1px solid ${Theme.colors.primary}`,
                              }}
                              disabled={isLoading}
                              onClick={() => !isLoading && setIsModalOpen(true)}
                            >
                              Change branding colors
                            </button>
                          </div>
                        </div>
                        {/* Theme Selection */}
                        {/* Creativity Slider */}
                        <div
                          className="flex flex-col space-y-3 p-4 rounded-lg shadow-sm"
                          style={{
                            backgroundColor: Theme.colors.stroke.weak,
                          }}
                        >
                          {/* Label, Info Icon, and Value Display */}
                          <div className="flex justify-between gap-1 items-center">
                            <div className="relative text-sm leading-snug">
                              <span
                                className="inline-flex items-center gap-1 flex-wrap font-semibold text-base"
                                style={{ color: Theme.colors.text.strong }}
                              >
                                Adjust Creativity
                                <span
                                  className="cursor-pointer"
                                  onMouseEnter={() =>
                                    setShowCreativityTooltip(true)
                                  }
                                  onMouseLeave={() =>
                                    setShowCreativityTooltip(false)
                                  }
                                  style={{ color: Theme.colors.primary }}
                                >
                                  <FaInfoCircle className="text-base inline" />
                                  {showCreativityTooltip && (
                                    <div
                                      className="hidden lg:block absolute text-left left-1/2 bottom-8 w-72 p-3 text-xs rounded-lg shadow-lg opacity-100 transform -translate-x-1/2 transition-opacity duration-300"
                                      style={{
                                        backgroundColor: Theme.colors.grounding,
                                        color: Theme.colors.background,
                                      }}
                                    >
                                      <p className="font-semibold">
                                        How does Creativity affect text?
                                      </p>
                                      <p>
                                        Adjust how creative or structured the
                                        generated text should be...
                                      </p>
                                    </div>
                                  )}
                                </span>
                              </span>
                            </div>

                            <span
                              className="font-medium"
                              style={{ color: Theme.colors.text.strong }}
                            >
                              Creativity: {creativity.toFixed(1)}
                            </span>
                          </div>
                          {/* Styled Slider */}
                          <input
                            type="range"
                            min="0"
                            max="10"
                            step="0.1"
                            value={creativity}
                            onChange={(e) => {
                              let newValue = Number(e.target.value);
                              newValue = Math.max(0, Math.min(10, newValue)); // Keep within range
                              setCreativity(newValue);
                            }}
                            className="w-full h-2 rounded-lg transition-all appearance-none "
                            style={{
                              background: `linear-gradient(to right, ${
                                Theme.colors.primary
                              } ${(creativity / 10) * 100}%, white ${
                                (creativity / 10) * 100
                              }%)`,
                              color: Theme.colors.primary,
                            }}
                          />
                          {/* Indicator Bar with Tooltips */}
                          <div
                            className="flex justify-between text-xs font-medium mt-2"
                            style={{ color: Theme.colors.text.weak }}
                          >
                            <div className="relative group">
                              <span
                                className={
                                  creativity === 0
                                    ? "font-bold text-center"
                                    : "text-center"
                                }
                                style={{
                                  color:
                                    creativity === 0
                                      ? Theme.colors.primary
                                      : undefined,
                                }}
                              >
                                Most Structured
                              </span>
                              <div
                                className="hidden lg:absolute lg:top-full lg:left-1/2 lg:transform lg:-translate-x-1/2 lg:mt-2 w-max text-xs rounded px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
                                style={{
                                  backgroundColor: Theme.colors.grounding,
                                  color: Theme.colors.background,
                                }}
                              >
                                Highly precise and factual text with minimal
                                variation.
                              </div>
                            </div>

                            <div className="relative group">
                              <span
                                className={
                                  creativity >= 4.5 && creativity <= 5.5
                                    ? "font-bold text-center"
                                    : "text-center"
                                }
                                style={{
                                  color:
                                    creativity >= 4.5 && creativity <= 5.5
                                      ? Theme.colors.primary
                                      : undefined,
                                }}
                              >
                                Balanced
                              </span>
                              <div
                                className="hidden lg:absolute lg:top-full lg:left-1/2 lg:transform lg:-translate-x-1/2 lg:mt-2 w-max text-xs rounded px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
                                style={{
                                  backgroundColor: Theme.colors.grounding,
                                  color: Theme.colors.background,
                                }}
                              >
                                A mix of structured content with slight
                                variations for engagement.
                              </div>
                            </div>

                            <div className="relative group">
                              <span
                                className={
                                  creativity === 10
                                    ? "font-bold text-center ml-2 xs:ml-0"
                                    : "text-center"
                                }
                                style={{
                                  color:
                                    creativity === 10
                                      ? Theme.colors.primary
                                      : undefined,
                                }}
                              >
                                Most Creative
                              </span>
                              <div
                                className="hidden lg:absolute lg:top-full lg:left-1/2 lg:transform lg:-translate-x-1/2 lg:mt-2 w-max text-xs rounded px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
                                style={{
                                  backgroundColor: Theme.colors.grounding,
                                  color: Theme.colors.background,
                                }}
                              >
                                Free-flowing, expressive text with maximum
                                variation.
                              </div>
                            </div>
                          </div>{" "}
                        </div>{" "}
                        {/* Reference Web URL */}
                        {/* <div className="space-y-2 mt-6">
                          <label
                            className="flex items-center gap-2 text-sm font-medium"
                            style={{ color: Theme.colors.text.weak }}
                          >
                            Reference Web URL
                            <div className="relative group">
                              <FaInfoCircle
                                className="cursor-pointer"
                                style={{ color: Theme.colors.text.weak }}
                              />
                              <div className="hidden group-hover:block absolute z-10 left-full ml-2 -translate-y-1/2 w-64 p-2 text-xs rounded-lg shadow-lg bg-gray-800 text-white">
                                Add a specific website URL that will be used as
                                a reference for generating content in your
                                presentation.
                              </div>
                            </div>
                          </label>
                          <input
                            type="url"
                            value={referenceWebUrl}
                            onChange={(e) => setReferenceWebUrl(e.target.value)}
                            placeholder="https://example.com"
                            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2"
                            style={{
                              backgroundColor: Theme.colors.background,
                              color: Theme.colors.text.strong,
                              borderColor: Theme.colors.stroke.weak,
                              outlineColor: Theme.colors.primary,
                            }}
                          />
                        </div>{" "} */}
                        {/* Web Search Toggle */}
                        {/* <div className="space-y-2 mt-6">
                          <div className="flex items-center justify-between">
                            <label
                              className="flex items-center gap-2 text-sm font-medium"
                              style={{ color: Theme.colors.text.weak }}
                            >
                              Include Web Search
                              <div className="relative group">
                                <FaInfoCircle
                                  className="cursor-pointer"
                                  style={{ color: Theme.colors.text.weak }}
                                />
                                <div className="hidden group-hover:block absolute z-10 left-full ml-2 -translate-y-1/2 w-64 p-2 text-xs rounded-lg shadow-lg bg-gray-800 text-white">
                                  Allow the AI to search the web for up-to-date
                                  information to include in your presentation.
                                </div>
                              </div>
                            </label>
                            {/* <div className="flex items-center">
                              <button
                                type="button"
                                onClick={() =>
                                  setIncludeWebSearch(!includeWebSearch)
                                }
                                className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1`}
                                style={{
                                  backgroundColor: includeWebSearch
                                    ? Theme.colors.primary
                                    : Theme.colors.stroke.weak,
                                  boxShadow: `0 0 0 1px ${
                                    includeWebSearch
                                      ? Theme.colors.primary
                                      : "transparent"
                                  }`,
                                }}
                              >
                                <span
                                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                                    includeWebSearch
                                      ? "translate-x-6"
                                      : "translate-x-1"
                                  }`}
                                />
                              </button>
                              <span
                                className="ml-3 text-sm"
                                style={{ color: Theme.colors.text.strong }}
                              >
                                {includeWebSearch ? "Enabled" : "Disabled"}
                              </span>
                            </div> */}
                        {/* </div>
                        </div>{" "} */}
                        {/* Technical Depth Dropdown */}
                        <div className="space-y-2 mt-6">
                          <label
                            className="flex items-center gap-2 text-sm font-medium"
                            style={{ color: Theme.colors.text.weak }}
                          >
                            Technical Depth
                            <div className="relative group">
                              <FaInfoCircle
                                className="cursor-pointer"
                                style={{ color: Theme.colors.text.weak }}
                              />
                              <div className="hidden group-hover:block absolute z-10 left-full ml-2 -translate-y-1/2 w-64 p-2 text-xs rounded-lg shadow-lg bg-gray-800 text-white">
                                Choose how technical the content should be in
                                your presentation.
                              </div>
                            </div>
                          </label>
                          <select
                            value={technicalDepth}
                            onChange={(e) => setTechnicalDepth(e.target.value)}
                            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2"
                            style={{
                              backgroundColor: Theme.colors.background,
                              color: Theme.colors.text.strong,
                              borderColor: Theme.colors.stroke.weak,
                              outlineColor: Theme.colors.primary,
                            }}
                          >
                            <option value="Auto">Auto</option>
                            <option value="Basic">Basic</option>
                            <option value="Medium">Moderate</option>
                            <option value="Advanced">Advanced</option>
                            <option value="Expert">Expert</option>
                          </select>
                        </div>
                        {/* Generate Document Button */}
                        <div className="flex w-full justify-center mt-16">
                          <button
                            disabled={
                              isLoading ||
                              (websiteUrl.length > 0 && !isValidLink)
                            }
                            onClick={handleButtonClick}
                            className={`lg:w-1/2 w-[80%] py-2 rounded-lg font-semibold active:scale-95 transition transform duration-300  ${
                              isLoading ||
                              (websiteUrl.length > 0 && !isValidLink)
                                ? "cursor-not-allowed"
                                : "hover:opacity-90"
                            }`}
                            style={{
                              backgroundColor:
                                isLoading ||
                                (websiteUrl.length > 0 && !isValidLink)
                                  ? Theme.colors.stroke.weak
                                  : Theme.colors.primary,
                              color:
                                isLoading ||
                                (websiteUrl.length > 0 && !isValidLink)
                                  ? Theme.colors.text.weak
                                  : "#FFFFFF",
                            }}
                          >
                            Generate Document
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Color Selection Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-[40]">
          <div
            className="p-6 rounded-2xl shadow-lg w-[90%] max-w-md relative"
            style={{ backgroundColor: Theme.colors.background }}
          >
            <h2
              className="text-xl font-bold mb-4 text-center"
              style={{ color: Theme.colors.text.strong }}
            >
              Select Branding Colors
            </h2>

            <div className="space-y-4">
              {/* Primary Color */}
              <div className="grid grid-cols-3 items-center gap-2">
                <label
                  className="font-semibold"
                  style={{ color: Theme.colors.text.weak }}
                >
                  Primary Color:
                </label>
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-24 p-2 border rounded-lg text-center shadow-sm focus:ring-2 outline-none"
                  style={{
                    borderColor: Theme.colors.stroke.weak,
                    color: Theme.colors.text.strong,
                    fontFamily: Theme.font.family,
                    backgroundColor: Theme.colors.background,
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                  }}
                />
                <div className="relative w-12 h-12">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div
                    className="w-12 h-12 rounded-full border shadow-md cursor-pointer transition-transform hover:scale-110"
                    style={{
                      backgroundColor: primaryColor,
                      borderColor: Theme.colors.stroke.weak,
                    }}
                  />
                </div>
              </div>

              {/* Secondary Color */}
              <div className="grid grid-cols-3 items-center gap-2">
                <label
                  className="font-semibold"
                  style={{ color: Theme.colors.text.weak }}
                >
                  Secondary Color:
                </label>
                <input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="w-24 p-2 border rounded-lg text-center shadow-sm focus:ring-2 outline-none"
                  style={{
                    borderColor: Theme.colors.stroke.weak,
                    color: Theme.colors.text.strong,
                    fontFamily: Theme.font.family,
                    backgroundColor: Theme.colors.background,
                    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                  }}
                />
                <div className="relative w-12 h-12">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div
                    className="w-12 h-12 rounded-full border shadow-md cursor-pointer transition-transform hover:scale-110"
                    style={{
                      backgroundColor: secondaryColor,
                      borderColor: Theme.colors.stroke.weak,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Modal Buttons */}
            <div className="mt-6 flex justify-end space-x-4">
              <button
                className="px-4 py-2 rounded-lg transition-all"
                style={{
                  backgroundColor: Theme.colors.stroke.weak,
                  color: Theme.colors.text.weak,
                }}
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg transition-all shadow-md"
                style={{
                  backgroundColor: Theme.colors.primary,
                  color: "#FFFFFF",
                }}
                onClick={() => {
                  handleSaveColors();
                  setIsModalOpen(false);
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      <GuidedTour />
      <GuidedTourMobile />
    </div>
  );
};

export default DocumentInput;
const onOutlineSuccess = (success: boolean) => {
  if (success) {
    toast.success("Outline generated successfully!", {
      position: "top-right",
      autoClose: 3000,
    });
  } else {
    toast.error("Failed to generate outline. Please try again.", {
      position: "top-right",
      autoClose: 3000,
    });
  }
};
